// agoraRtm.js
// @see https://docs.agora.io/en/signaling/reference/api?platform=web
'use strict';
import { realtime as realtimeDebug } from '../resource/debug.js';

const realtimeLog = realtimeDebug.extend('log:agoraRtm');
const realtimeError = realtimeDebug.extend('error:agoraRtm');

let AgoraRtm;
let loadAgoraRtmPromise;

let rtmInstance;
let lastListeners = [];

/**
 * Load Agora RTM module and initialize agora rtm instance.
 * @param {object} payload - function payload.
 * @param {string} payload.appId - agora app id.
 * @param {string} payload.sessionId - agora session id.
 * @param {object} payload.options - agora rtm options.
 * @return {Promise<AgoraRtm>} AgoraRtm instance.
 */
const init = async ({ appId, sessionId, options } = {}) => {
  const log = realtimeLog.extend('init');
  const errorLog = realtimeError.extend('init');

  try {
    log('start init agora rtm', { appId, sessionId, options });

    // Only import AgoraRTM in browser environment
    if (typeof window === 'undefined') {
      throw new Error(
        'AgoraRTM can only be initialized in browser environment'
      );
    }

    if (!AgoraRtm) {
      log('start load agora rtm sdk', {
        AgoraRtm,
        loadAgoraRtmPromise,
      });
      if (!loadAgoraRtmPromise) {
        loadAgoraRtmPromise = import('agora-rtm-sdk');
      }
      AgoraRtm = (await loadAgoraRtmPromise).default;
    }

    log('agora rtm sdk loaded', {
      AgoraRtm,
      loadAgoraRtmPromise,
    });

    const { RTM } = AgoraRtm;

    if (rtmInstance) {
      log('reuse existing rtm instance', rtmInstance);
      return rtmInstance;
    }

    log('create new rtm instance', { appId, sessionId, options });
    const instance = new RTM(appId, sessionId, options);

    rtmInstance = instance;
    window._agora_rtm_instance = instance;

    log('rtm instance created', {
      appId,
      sessionId,
      options,
      instance,
    });

    return instance;
  } catch (error) {
    errorLog('fail to init agora rtm', { appId, sessionId, options, error });
    throw error;
  }
};

/**
 * Set event listeners for agora rtm instance.
 * This function will unbind the existing event listeners. Then bind the new event listeners.
 *
 * @param {object} payload - function payload.
 * @param {Array<{name: string, handler: function}>} payload.listeners - empty array means unbind all existing event listeners.
 * @return {Promise} Promise.
 */
const setEventListeners = async ({ listeners = [] } = {}) => {
  const log = realtimeLog.extend('setEventListeners');
  const errorLog = realtimeError.extend('setEventListeners');

  try {
    log('start set event listeners', { listeners });

    lastListeners.forEach(item => {
      const { name, handler } = item;
      rtmInstance.removeEventListener(name, handler);
    });

    listeners.forEach(item => {
      const { name, handler } = item;
      rtmInstance.addEventListener(name, handler);
    });

    lastListeners = listeners;

    log('done set event listeners', { listeners });
  } catch (error) {
    errorLog('fail to set event listeners', {
      listeners,
      error,
    });
  }
};

/**
 * Login to agora rtm.
 * @param {object} payload - function payload.
 * @param {string} payload.token - agora rtm token.
 * @return {Promise} Promise.
 */
const login = async ({ token } = {}) => {
  const log = realtimeLog.extend('login');
  const errorLog = realtimeError.extend('login');

  try {
    log('start login agora rtm', { token });
    const result = await rtmInstance.login({ token });
    log('login agora rtm done', { token, result });

    return result;
  } catch (error) {
    errorLog('fail to login agora rtm', { token, error });
    throw error;
  }
};

/**
 * Join/leave stream channel for agora rtm.
 * @param {object} payload - function payload.
 * @param {string} payload.channelName - stream channel name.
 * @param {string} payload.token - stream channel token.
 * @param {boolean} payload.isJoinChannel - whether to join stream channel.
 * @param {object} payload.options - stream channel options.
 * @return {Promise} Promise.
 */
const manageStreamChannel = async ({
  channelName,
  token,
  isJoinChannel = true,
  options = {},
} = {}) => {
  const log = realtimeLog.extend('manageStreamChannel');
  const errorLog = realtimeError.extend('manageStreamChannel');

  try {
    log('start manage stream channel', {
      channelName,
      token,
      isJoinChannel,
      options,
    });

    const streamChannel = await rtmInstance.createStreamChannel(channelName);

    log('stream channel created', {
      channelName,
      token,
      isJoinChannel,
      options,
      streamChannel,
    });

    let result;

    if (isJoinChannel) {
      result = await streamChannel.join({
        token,
        ...options,
      });
    } else {
      result = await streamChannel.leave();
    }

    log('manage stream channel done', {
      channelName,
      token,
      isJoinChannel,
      options,
      result,
    });

    return result;
  } catch (error) {
    errorLog('fail to manage stream channel', {
      channelName,
      token,
      isJoinChannel,
      options,
      error,
    });
    throw error;
  }
};

/**
 * Join/leave message channel for agora rtm.
 * @param {object} payload - function payload.
 * @param {string} payload.channelName - message channel name.
 * @param {boolean} payload.isJoinChannel - whether to join message channel.
 * @param {object} payload.options - message channel options.
 * @return {Promise} Promise.
 */
const manageMessageChannel = async ({
  channelName,
  isJoinChannel = true,
  options = {},
} = {}) => {
  const log = realtimeLog.extend('manageMessageChannel');
  const errorLog = realtimeError.extend('manageMessageChannel');

  try {
    log('start manage message channel', {
      channelName,
      isJoinChannel,
      options,
    });

    let result;

    if (isJoinChannel) {
      result = await rtmInstance.subscribe(channelName, options);
    } else {
      result = await rtmInstance.unsubscribe(channelName);
    }

    log('manage message channel done', {
      channelName,
      isJoinChannel,
      options,
      result,
    });

    return result;
  } catch (error) {
    errorLog('fail to manage message channel', { error });
    throw error;
  }
};

/**
 * Renew token for agora rtm.
 * @param {object} payload - function payload.
 * @param {string} payload.token - agora rtm token.
 * @param {object} payload.options - agora rtm options.
 * @return {Promise} Promise.
 */
const renewToken = async ({ token, options } = {}) => {
  const log = realtimeLog.extend('renewToken');
  const errorLog = realtimeError.extend('renewToken');

  try {
    log('start renew token', {
      token,
      options,
    });

    await rtmInstance.renewToken(token, options);

    log('renew token done', {
      token,
      options,
    });
  } catch (error) {
    errorLog('fail to renew token', { error });
    throw error;
  }
};

export {
  rtmInstance,
  init,
  setEventListeners,
  login,
  manageStreamChannel,
  manageMessageChannel,
  renewToken,
};
